import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { HiOutlineLockClosed, HiOutlineSparkles, HiOutlineUserAdd } from 'react-icons/hi';
import InviteFormModal from '../InviteFormModal/InviteFormModal';

/**
 * ProcedurePaywall — inline paywall shown below partial procedure data.
 * Shows different CTAs for authenticated vs. unauthenticated users.
 * Rendered inside detail pages when backend returns isPartial: true.
 */
const ProcedurePaywall = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showInviteModal, setShowInviteModal] = useState(false);

  if (isAuthenticated) {
    // Authenticated but no subscription
    return (
      <div className="mt-8 mb-12 flex justify-center px-4">
        <div className="max-w-lg w-full text-center">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-5">
            <HiOutlineLockClosed className="w-8 h-8 text-amber-600" />
          </div>

          <h2 className="text-xl font-bold text-gray-800 mb-3">
            Activeaza un abonament
          </h2>

          <p className="text-gray-500 text-sm leading-relaxed mb-5">
            Aceasta pagina contine informatii detaliate disponibile exclusiv pentru utilizatorii cu abonament activ.
            Alege un plan lunar sau anual pentru a debloca accesul complet.
          </p>

          <div className="bg-amber-50 rounded-xl p-5 mb-5 text-left border border-amber-200">
            <h3 className="text-sm font-bold text-amber-700 mb-3 flex items-center gap-2">
              <HiOutlineSparkles className="w-4 h-4" />
              Cu un abonament Licitatii-Constructii ai acces la:
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">&#10003;</span>
                Detalii complete ale procedurilor (autoritate, documente, valori)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">&#10003;</span>
                Vizualizare proiecte si proceduri asociate
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">&#10003;</span>
                Alerte zilnice personalizate pe email
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">&#10003;</span>
                Generare oferte tehnice cu AI
              </li>
            </ul>
          </div>

          <button
            onClick={() => navigate('/abonamente')}
            className="px-6 py-3 bg-amber-600 text-white font-semibold rounded-xl hover:bg-amber-700 transition shadow-lg shadow-amber-200"
          >
            Vezi planurile de abonament
          </button>
        </div>
      </div>
    );
  }

  // Not authenticated
  return (
    <div className="mt-8 mb-12 flex justify-center px-4">
      <div className="max-w-lg w-full text-center">
        <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-5">
          <HiOutlineUserAdd className="w-8 h-8 text-indigo-600" />
        </div>

        <h2 className="text-xl font-bold text-gray-800 mb-3">
          Creeaza-ti un cont Licitatii-Constructii
        </h2>

        <p className="text-gray-500 text-sm leading-relaxed mb-5">
          Pentru a accesa detaliile complete ale procedurilor de achizitii publice,
          creeaza-ti un cont si activeaza un abonament lunar sau anual.
        </p>

        <div className="bg-indigo-50 rounded-xl p-5 mb-5 text-left border border-indigo-200">
          <h3 className="text-sm font-bold text-indigo-700 mb-3 flex items-center gap-2">
            <HiOutlineSparkles className="w-4 h-4" />
            De ce Licitatii-Constructii?
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-0.5">&#10003;</span>
              Monitorizare licitatii din constructii in timp real
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-0.5">&#10003;</span>
              Alerte personalizate pe coduri CPV si cuvinte cheie
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-0.5">&#10003;</span>
              Generare automata oferte tehnice cu AI
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-0.5">&#10003;</span>
              Proiecte agregate cu vizualizare pipeline
            </li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-200"
          >
            Vreau invitație
          </button>
          <button
            onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`)}
            className="px-6 py-3 bg-white text-indigo-600 font-semibold rounded-xl border-2 border-indigo-200 hover:bg-indigo-50 transition"
          >
            Am deja cont — Autentifica-te
          </button>
        </div>
      </div>

      <InviteFormModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />
    </div>
  );
};

export default ProcedurePaywall;
